import { Popconfirm, Select, Tag, Tooltip, Typography } from 'antd'
import { useState } from 'react'

import { Paragraph } from 'src/components'
import { useLocales } from 'src/locales'

import { useSystemKeys } from '../../hooks/useSystemKeys'

type Props = {
  value?: string | null
  onChange?: (value: string | null) => void
  isEditing: boolean
  elementId?: string
  systemKeys: ReturnType<typeof useSystemKeys>
  /** Called with the id of the element the selected unique key is moved from (undefined when no move). */
  onMoveFromChange: (holderElementId?: string) => void
}

/**
 * Form 058-1/e system key of a classifier element: a tag in read mode, a select in edit mode.
 * Selecting a unique key held by another element asks to move it from that element.
 */
export const SystemKeyField = ({
  value,
  onChange,
  isEditing,
  elementId,
  systemKeys,
  onMoveFromChange
}: Props) => {
  const { t } = useLocales()
  const { keys, holderNames, getLabel, findKey } = systemKeys

  const [pendingMove, setPendingMove] = useState<{
    key: string
    holderElementId: string
  }>()

  const current = findKey(value)
  // An OriginalValue that is not one of the classifier's system keys is owned by the system.
  const isSystemManaged = !!value && !current

  const getHolderId = (key?: string | null) => {
    const holderElementId = findKey(key)?.holderElementId

    return holderElementId && holderElementId !== elementId
      ? holderElementId
      : undefined
  }

  if (!isEditing) {
    return current ? (
      <Tag color="blue">{getLabel(current)}</Tag>
    ) : (
      <Paragraph>{isSystemManaged ? value : '-'}</Paragraph>
    )
  }

  const onSelect = (key?: string) => {
    const holderElementId = getHolderId(key)

    if (key && holderElementId) {
      setPendingMove({ key, holderElementId })
      return
    }

    onChange?.(key ?? null)
    onMoveFromChange(undefined)
  }

  const onConfirmMove = () => {
    if (!pendingMove) return

    onChange?.(pendingMove.key)
    onMoveFromChange(pendingMove.holderElementId)
    setPendingMove(undefined)
  }

  const options = keys.map((item) => {
    const holderElementId = getHolderId(item.key)
    const label = getLabel(item)

    return {
      value: item.key,
      label: holderElementId ? (
        <Tooltip
          placement="left"
          title={t('classifiers.system_key_held_by', {
            name: holderNames[holderElementId] ?? ''
          })}
        >
          <Typography.Text type="secondary">{label}</Typography.Text>
        </Tooltip>
      ) : (
        label
      )
    }
  })

  return (
    <Popconfirm
      open={!!pendingMove}
      title={t('classifiers.system_key_move', {
        name: pendingMove
          ? (holderNames[pendingMove.holderElementId] ?? '')
          : ''
      })}
      onConfirm={onConfirmMove}
      onCancel={() => setPendingMove(undefined)}
    >
      <Select
        allowClear
        className="full-width"
        disabled={isSystemManaged}
        value={value ?? undefined}
        options={isSystemManaged ? [{ value, label: value }] : options}
        placeholder={t('classifiers.system_key')}
        onChange={onSelect}
      />
    </Popconfirm>
  )
}
